import type { RouteLocationNormalized, RouteLocationRaw, RouteRecordName, RouteRecordRaw } from "vue-router";

const routePathMap: Record<string, Record<string, string>> = {
  // DEFAULT
  CompareProducts: { en: "/compare", nl: "/product-vergelijken", de: "/produkt-vergleichen" },
  ForgotPassword: { en: "/forgot-password", nl: "/wachtwoord-vergeten", de: "/passwort-vergessen" },
  Search: { en: "/search", nl: "/zoeken", de: "/suchen" },
  SignIn: { en: "/sign-in", nl: "/inloggen", de: "/anmelden" },
  SignUp: { en: "/sign-up", nl: "/registreren", de: "/konto-anlegen" },

  // CATALOG AND CHECKOUT
  Cart: { en: "/cart", nl: "/winkelwagen", de: "/warenkorb" },
  Catalog: { en: "/catalog", nl: "/catalogus", de: "/katalog" },
  Brands: { en: "/brands", nl: "/merken", de: "/marken" },
  Category: { en: "/category/:categoryId", nl: "/categorie/:categoryId", de: "/kategorie/:categoryId" },
  Checkout: { en: "/checkout", nl: "/afrekenen", de: "/kasse" },
  CheckoutCompleted: { en: "/checkout/completed", nl: "/afrekenen/afgerond", de: "/kasse/vollendet" },
  CheckoutPayment: { en: "/checkout/payment", nl: "/afrekenen/betaling", de: "/kasse/zahlung" },
  CheckoutPaymentResult: {
    en: "/checkout/payment/:status(success|failure)",
    nl: "/afrekenen/betaling/:status(success|failure)",
    de: "/kasse/zahlung/:status(success|failure)",
  },
  Product: { en: "/product/:productId", nl: "/product/:productId", de: "/produkt/:productId" },
  Review: { en: "/checkout/review", nl: "/afrekenen/overzicht", de: "/kasse/uebersicht" },
  Shipping: { en: "/checkout/shipping", nl: "/afrekenen/verzenden", de: "/kasse/versand" },

  // ACCOUNT
  AccountBulkOrder: { en: "/account/bulk-order", nl: "/account/bulk-order", de: "/konto/grossbestellung" },
  Addresses: { en: "/account/addresses", nl: "/account/adressen", de: "/konto/adressen" },
  ChangePasswordAccount: {
    en: "/account/change-password",
    nl: "/account/verander-wachtwoord",
    de: "/konto/passwort-aendern",
  },
  CompanyInfo: { en: "/company/info", nl: "/bedrijf/informatie", de: "/unternehmen/informationen" },
  CompanyMembers: { en: "/company/members", nl: "/bedrijf/medewerkers", de: "/unternehmen/mitglieder" },
  Dashboard: { en: "/account/dashboard", nl: "/account/dashboard", de: "/konto/dashboard" },
  ImpersonateUserDashboard: {
    en: "/impersonate-user",
    nl: "/impersonaliseer-gebruiker",
    de: "/identitaetswechsel-benutzer",
  },
  ListDetails: { en: "/account/list/:listId", nl: "/account/lijst/:listId", de: "/konto/list/:listId" },
  Lists: { en: "/account/lists", nl: "/account/lijsten", de: "/konto/listen" },
  OrderDetails: {
    en: "/account/orders/:orderId",
    nl: "/account/bestellingen/:orderId",
    de: "/konto/bestellungen/:orderId",
  },
  OrderPayment: {
    en: "/account/orders/:orderId/payment",
    nl: "/account/bestellingen/:orderId/betaling",
    de: "/konto/bestellungen/:orderId/zahlung",
  },
  Orders: { en: "/account/orders", nl: "/account/bestellingen", de: "/konto/bestellungen" },
  Profile: { en: "/account/profile", nl: "/account/profiel", de: "/konto/profil" },
};

function extractParamNames(path: string): Set<string> {
  const params = new Set<string>();
  const regex = /:(\w+)/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(path)) !== null) {
    params.add(match[1]);
  }
  return params;
}

function fillPathParams(template: string, params: Record<string, unknown>): string {
  return template.replace(/:(\w+)/g, (_match, key: string) => {
    const value = params[key];
    let raw: unknown = value;

    if (Array.isArray(value)) {
      raw = (value as unknown[])[0];
    }

    let str = "";
    if (raw !== undefined && raw !== null) {
      if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
        str = String(raw);
      }
    }

    return encodeURIComponent(str);
  });
}

function getLocalizedPathForName(
  name: RouteRecordName | null | undefined,
  locale: string,
  params = {},
): string | undefined {
  if (!name) {
    return undefined;
  }

  const perLocale = routePathMap[String(name)];
  const template = perLocale?.[locale];

  if (!template) {
    return undefined;
  }

  return fillPathParams(template, params);
}

function tryMatchTemplateAndExtractParams(template: string, path: string): Record<string, string> | null {
  // Convert ":param" to named groups
  const pattern = template.replace(/:\w+/g, (segment) => `(?<${segment.slice(1)}>[^/]+)`);
  const regex = new RegExp(`^${pattern}$`);
  const groups = regex.exec(path)?.groups;
  if (!groups) {
    return null;
  }
  return groups as Record<string, string>;
}

function splitLocalePrefix(path: string): { localePrefix: string; pathWithoutLocale: string } {
  const match = path.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i);
  if (!match) {
    return { localePrefix: "", pathWithoutLocale: path };
  }

  const localePrefix = `/${match[1]}`;
  const pathWithoutLocale = path.slice(localePrefix.length) || "/";
  return { localePrefix, pathWithoutLocale };
}

function withLocalePrefix(path: string, localePrefix: string): string {
  if (!localePrefix) {
    return path;
  }

  if (path === "/") {
    return localePrefix;
  }

  return `${localePrefix}${path}`;
}

function getLocalizedPathForPath(path: string, locale: string): string | undefined {
  const { localePrefix, pathWithoutLocale } = splitLocalePrefix(path);

  for (const perLocale of Object.values(routePathMap)) {
    for (const template of Object.values(perLocale)) {
      const params = tryMatchTemplateAndExtractParams(template, pathWithoutLocale);
      if (!params) {
        continue;
      }

      const targetTemplate = perLocale[locale];
      if (!targetTemplate) {
        return undefined;
      }

      return withLocalePrefix(fillPathParams(targetTemplate, params), localePrefix);
    }
  }

  // Support localized redirects for nested static paths like /brands/:slug -> /merken/:slug.
  for (const perLocale of Object.values(routePathMap)) {
    const staticTemplates = Object.values(perLocale)
      .filter((template) => !template.includes(":"))
      .sort((a, b) => b.length - a.length);

    for (const template of staticTemplates) {
      if (pathWithoutLocale !== template && !pathWithoutLocale.startsWith(`${template}/`)) {
        continue;
      }

      const targetTemplate = perLocale[locale];
      if (!targetTemplate || targetTemplate.includes(":")) {
        continue;
      }

      const suffix = pathWithoutLocale.slice(template.length);
      return withLocalePrefix(`${targetTemplate}${suffix}`, localePrefix);
    }
  }

  return undefined;
}

/**
 * This function is used to add localized aliases to the routes.
 */
export function withLocalizedAliases(routes: RouteRecordRaw[]): RouteRecordRaw[] {
  function enhanceRoute(route: RouteRecordRaw): RouteRecordRaw {
    const enhanced: RouteRecordRaw = { ...route };

    if (enhanced.children?.length) {
      enhanced.children = enhanced.children.map(enhanceRoute);
    }

    const name = enhanced.name ? String(enhanced.name) : undefined;
    if (!name || !Object.prototype.hasOwnProperty.call(routePathMap, name)) {
      return enhanced;
    }

    const perLocale = routePathMap[name];
    const basePath = enhanced.path;
    const baseParams = extractParamNames(basePath);

    const candidateAliases = Object.values(perLocale).filter((path) => path !== basePath);

    const validAliases = candidateAliases.filter((aliasPath) => {
      const aliasParams = extractParamNames(aliasPath);
      return baseParams.size === aliasParams.size && [...baseParams].every((p) => aliasParams.has(p));
    });

    let existingAliases: string[] = [];
    if (Array.isArray(enhanced.alias)) {
      existingAliases = enhanced.alias;
    } else if (typeof enhanced.alias === "string") {
      existingAliases = [enhanced.alias];
    }

    const merged = Array.from(new Set([...existingAliases, ...validAliases]));
    if (merged.length) {
      enhanced.alias = merged;
    }

    return enhanced;
  }

  return routes.map(enhanceRoute);
}

/**
 * This function returns a localized redirect if the current route is not the same as the localized path.
 */
export function resolveLocalizedRedirect(to: RouteLocationNormalized, locale: string): RouteLocationRaw | void {
  let localizedPath: string | undefined;

  if (to.name) {
    localizedPath = getLocalizedPathForName(to.name, locale, to.params);
  }

  // Fallback for matcher and any raw path navigation that should still be localized.
  if (!localizedPath) {
    localizedPath = getLocalizedPathForPath(to.path, locale);
  }

  if (localizedPath && localizedPath !== to.path) {
    return { path: localizedPath, query: to.query, hash: to.hash, replace: true };
  }

  return undefined;
}
